import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { Loader2, Search, ArrowRight, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

const INDEX_META = {
  destination: { hero: "https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=2000&q=80", intro: "Des lagons turquoise aux capitales vibrantes : explorez nos destinations sélectionnées et composez le voyage qui vous ressemble." },
  guide: { hero: "https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=2000&q=80", intro: "Nos guides de voyage détaillés — quand partir, budget, formalités et incontournables — pour préparer chaque étape sereinement." },
  honeymoon: { hero: "https://images.unsplash.com/photo-1518495973542-4542c06a5843?w=2000&q=80", intro: "Des séjours pensés pour célébrer votre amour : hôtels romantiques, expériences rares et instants suspendus." },
  inspiration: { hero: "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?w=2000&q=80", intro: "Laissez-vous porter par nos inspirations voyage — idées d'évasion, tendances et coups de cœur de nos conseillers." },
  circuit: { hero: "https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=2000&q=80", intro: "Des itinéraires soigneusement composés pour découvrir l'essentiel d'une région, à votre rythme." },
  hotel: { hero: "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=2000&q=80", intro: "Une sélection d'adresses d'exception, choisies pour leur âme, leur emplacement et leur service." },
  faq: { hero: "https://images.unsplash.com/photo-1521295121783-8a321d551ad2?w=2000&q=80", intro: "Toutes les réponses à vos questions sur nos voyages sur mesure, réservations et services." },
  conseils: { hero: "https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=2000&q=80", intro: "Nos conseils pratiques pour voyager l'esprit léger, avant et pendant votre séjour." },
  blog: { hero: "https://images.unsplash.com/photo-1499591934245-40b55745b905?w=2000&q=80", intro: "Récits, actualités et regards sur le voyage par l'équipe DealTravel." },
  pays: { hero: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=2000&q=80", intro: "Explorez nos pays de prédilection et leurs trésors." },
  region: { hero: "https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=2000&q=80", intro: "Régions de caractère à découvrir au fil de vos envies." },
  ville: { hero: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=2000&q=80", intro: "Villes d'exception, entre patrimoine et art de vivre." },
  sur_mesure: { hero: "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=2000&q=80", intro: "Le voyage entièrement composé selon vos envies, votre rythme et votre budget." },
};

const DEFAULT_HERO = "https://images.pexels.com/photos/9400976/pexels-photo-9400976.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=2000";

export default function PublicContentIndex({ contentType }) {
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | error
  const [search, setSearch] = useState("");
  const [tag, setTag] = useState(null);
  const debounce = useRef(null);

  const fetchIndex = (opts = {}) => {
    const params = {};
    if (opts.search) params.search = opts.search;
    if (opts.tag) params.tag = opts.tag;
    api.get(`/public/index/${contentType}`, { params })
      .then((r) => { setData(r.data); setState("ok"); })
      .catch(() => setState("error"));
  };

  // initial + tag change (immediate)
  useEffect(() => {
    setState((s) => (data ? s : "loading"));
    fetchIndex({ search, tag });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentType, tag]);

  // search change (debounced)
  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => fetchIndex({ search, tag }), 350);
    return () => debounce.current && clearTimeout(debounce.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const meta = INDEX_META[contentType] || { hero: DEFAULT_HERO, intro: "" };
  const label = data?.label || "";
  const heroImg = (data?.items?.[0]?.cover_image) || meta.hero;
  const isEmpty = state === "ok" && (data?.items?.length || 0) === 0 && !search && !tag;

  const title = `${label} | DealTravel`;
  const desc = meta.intro || `Découvrez nos ${label?.toLowerCase()} et composez votre voyage sur mesure avec DealTravel.`;
  const canonical = `${window.location.origin}/${data?.prefix || contentType}`;

  return (
    <div className="min-h-screen bg-[#FAF9F6] text-[#1A1B18]">
      <Helmet>
        <html lang="fr" />
        <title>{title}</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href={canonical} />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="DealTravel" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
        <meta property="og:locale" content="fr_FR" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <Nav />

      {/* HERO */}
      <section className="relative min-h-[52vh] md:min-h-[62vh] flex items-end overflow-hidden bg-[#1A1B18]">
        <img src={heroImg} alt={label} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-black/25" />
        <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-12 pb-14 md:pb-16 pt-36 text-white">
          <p className="label-eyebrow text-[#E4DCCF]">Le magazine DealTravel</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.04] mt-4">{label}</h1>
          {meta.intro && <p className="mt-5 text-base md:text-lg text-white/85 max-w-[60ch] leading-relaxed">{meta.intro}</p>}
        </div>
      </section>

      {state === "loading" && (
        <div className="py-32 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#96836B]" /></div>
      )}

      {state === "error" && (
        <div className="py-32 text-center px-6">
          <p className="text-[#737373]">Une erreur est survenue. Merci de réessayer.</p>
        </div>
      )}

      {state === "ok" && (
        isEmpty
          ? <EmptyState label={label} contentType={contentType} heroImg={meta.hero} intro={meta.intro} />
          : (
            <section className="py-16 md:py-20 px-6 md:px-12">
              <div className="max-w-[1400px] mx-auto">
                {/* Toolbar: search + tags */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-12">
                  <div className="relative w-full md:max-w-sm">
                    <Search className="absolute left-0 top-1/2 -translate-y-1/2 h-4 w-4 text-[#96836B]" />
                    <input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder={`Rechercher un ${label?.toLowerCase().replace(/s$/, "") || "contenu"}…`}
                      data-testid="index-search"
                      className="w-full bg-transparent border-b border-[#D9D5CC] focus:border-[#1A1B18] outline-none pl-6 py-2.5 text-sm placeholder:text-[#9c968a] transition-colors"
                    />
                  </div>
                  {data?.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-2" data-testid="index-tags">
                      <button
                        onClick={() => setTag(null)}
                        className={`text-[11px] uppercase tracking-[0.18em] px-3.5 py-1.5 border transition-colors ${!tag ? "bg-[#1A1B18] text-white border-[#1A1B18]" : "border-[#D9D5CC] text-[#737373] hover:border-[#1A1B18] hover:text-[#1A1B18]"}`}
                      >
                        Tout
                      </button>
                      {data.tags.map((t) => (
                        <button
                          key={t.id}
                          onClick={() => setTag(tag === t.id ? null : t.id)}
                          data-testid={`index-tag-${t.slug}`}
                          className={`text-[11px] uppercase tracking-[0.18em] px-3.5 py-1.5 border transition-colors ${tag === t.id ? "bg-[#1A1B18] text-white border-[#1A1B18]" : "border-[#D9D5CC] text-[#737373] hover:border-[#1A1B18] hover:text-[#1A1B18]"}`}
                        >
                          {t.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Results */}
                {data?.items?.length === 0 ? (
                  <div className="py-20 text-center">
                    <p className="font-serif-display text-2xl">Aucun résultat</p>
                    <p className="text-[#737373] mt-2 text-sm">Essayez un autre mot-clé ou filtre.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12" data-testid="index-grid">
                    {data.items.map((it) => (
                      <Link key={it.id} to={it.url} data-testid={`index-card-${it.slug}`} className="group block">
                        <div className="aspect-[4/5] overflow-hidden bg-[#E4DCCF]">
                          {it.cover_image
                            ? <img src={it.cover_image} alt={it.cover_alt || it.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                            : <div className="h-full w-full bg-[#E4DCCF]" />}
                        </div>
                        <div className="mt-5 flex items-baseline justify-between gap-3">
                          <h2 className="font-serif-display text-2xl md:text-3xl leading-tight">{it.title}</h2>
                          <ArrowUpRight className="h-5 w-5 text-[#96836B] shrink-0 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all" />
                        </div>
                        {it.subtitle && <p className="text-xs uppercase tracking-[0.18em] text-[#96836B] mt-1.5">{it.subtitle}</p>}
                        {it.excerpt && <p className="text-sm text-[#737373] mt-2.5 leading-relaxed line-clamp-2">{it.excerpt}</p>}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </section>
          )
      )}

      <Footer />
    </div>
  );
}

const EmptyState = ({ label, contentType, heroImg, intro }) => {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/public/notify", { email, content_type: contentType });
      setSent(true);
      toast.success("Merci ! Vous serez averti·e des prochaines publications.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Adresse invalide.");
    }
  };
  return (
    <section className="py-20 md:py-28 px-6 md:px-12" data-testid="index-empty">
      <div className="max-w-[1100px] mx-auto grid md:grid-cols-2 gap-12 md:gap-16 items-center">
        <div className="aspect-[4/5] overflow-hidden bg-[#E4DCCF] order-last md:order-first">
          <img src={heroImg} alt={label} className="h-full w-full object-cover" />
        </div>
        <div>
          <p className="label-eyebrow">{label}</p>
          <h2 className="font-serif-display text-3xl md:text-4xl mt-4 leading-tight">Nos prochaines inspirations arrivent bientôt.</h2>
          <p className="text-[#737373] mt-5 leading-relaxed">
            {intro || "Notre équipe compose en ce moment une sélection d'exception."} En attendant, décrivez-nous votre voyage idéal — nous le construisons sur mesure, rien que pour vous.
          </p>
          <a href="/#devis" data-testid="empty-cta-devis" className="inline-flex items-center gap-2 mt-8 bg-[#1A1B18] text-white px-8 py-4 text-[11px] uppercase tracking-[0.24em] hover:bg-[#96836B] transition-colors">
            Composer mon séjour <ArrowRight className="h-4 w-4" />
          </a>

          {sent ? (
            <p className="mt-8 text-sm text-[#96836B]" data-testid="notify-sent">✓ Vous êtes bien inscrit·e. Nous vous préviendrons dès la première publication.</p>
          ) : (
            <form onSubmit={submit} className="mt-8 border-t border-[#EAE8E3] pt-6" data-testid="notify-form">
              <p className="text-sm text-[#4d4d4d] mb-3">Être averti·e des prochaines publications :</p>
              <div className="flex gap-2 max-w-md">
                <input
                  type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="votre@email.com" data-testid="notify-email"
                  className="flex-1 bg-transparent border-b border-[#D9D5CC] focus:border-[#1A1B18] outline-none py-2.5 text-sm placeholder:text-[#9c968a] transition-colors"
                />
                <button type="submit" data-testid="notify-submit" className="text-[11px] uppercase tracking-[0.22em] border border-[#1A1B18] px-5 py-2.5 hover:bg-[#1A1B18] hover:text-white transition-colors">
                  M'avertir
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
